import React, { useEffect } from "react";
import { Link } from "gatsby";
import { PrismicProvider } from "@prismicio/react";
import { PrismicPreviewProvider } from "gatsby-plugin-prismic-previews";

import { linkResolver } from "./src/utils/linkResolver";
import { repositoryConfigs } from "./src/utils/prismicPreviews";

// Import global styles
import "./src/styles/main.scss";

/**
 * Prismic Toolbar component
 * Adds the toolbar script and handles auto-refresh on content updates
 */
const PrismicToolbar = () => {
  useEffect(() => {
    const repoName = process.env.GATSBY_PRISMIC_REPO_NAME;
    if (!repoName) return;

    // Add Prismic toolbar script
    const script = document.createElement("script");
    script.src = `https://static.cdn.prismic.io/prismic.js?new=true&repo=${repoName}`;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    // Listen for Prismic toolbar events to auto-refresh
    const handlePrismicUpdate = async () => {
      if (process.env.NODE_ENV === "development") {
        try {
          await fetch("/__refresh", { method: "POST" });
          window.location.reload();
        } catch (e) {
          console.log("Auto-refresh not available");
        }
      }
    };

    window.addEventListener("prismicPreviewUpdate", handlePrismicUpdate);

    return () => {
      window.removeEventListener("prismicPreviewUpdate", handlePrismicUpdate);
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return null;
};

/**
 * Wrap the root element with Prismic providers
 * This enables link resolution and preview functionality
 */
/**
 * Sync body.hero-alt with ?alt=1 in the URL (e.g. /?alt=1)
 */
function syncHeroAltFromUrl() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const useAlt = params.get("alt") === "1";
  document.body.classList.toggle("hero-alt", useAlt);
}

export const onInitialClientRender = () => {
  syncHeroAltFromUrl();
};

export const onRouteUpdate = () => {
  syncHeroAltFromUrl();
};

export const wrapRootElement = ({ element }) => (
  <PrismicProvider
    linkResolver={linkResolver}
    internalLinkComponent={({ href, ...props }) => (
      <Link to={href} {...props} />
    )}
  >
    <PrismicPreviewProvider repositoryConfigs={repositoryConfigs}>
      <PrismicToolbar />
      {element}
    </PrismicPreviewProvider>
  </PrismicProvider>
);
